import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Tenant, TenantContextType } from '../types/tenant';
import { TenantService } from '../services/tenant-service';
import { useAuth } from './auth-context';
import { 
  getCurrentTenantSlug, 
  isMultiTenantMode, 
  applyTenantBranding, 
  createDefaultTenant,
  debugTenantDetection 
} from '../utils/tenant-utils';

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, session, loading: authLoading } = useAuth();

  const isMultiTenant = isMultiTenantMode();

  // Only initialize tenant when auth is complete and user is authenticated
  useEffect(() => {
    if (!authLoading) {
      if (session && user) {
        // User is authenticated - load their tenant
        initializeTenant();
      } else {
        // No authenticated user - use local fallback
        setTenant(createDefaultTenant());
        setLoading(false);
      }
    }
  }, [authLoading, session, user]);

  // Apply branding when tenant changes
  useEffect(() => {
    if (tenant?.branding) {
      applyTenantBranding(tenant.branding);
    }
  }, [tenant]);

  const initializeTenant = async () => {
    try {
      setLoading(true);
      setError(null);

      // Debug tenant detection in development
      if (import.meta.env.DEV) {
        debugTenantDetection();
      }

      let currentTenant: Tenant | null = null;

      // In V3, users don't have direct tenant_id - they access workspaces through memberships
      // Check URL first for specific tenant
      const tenantSlug = getCurrentTenantSlug();
      
      if (tenantSlug && tenantSlug !== 'default') {
        // Try to load specific tenant from URL
        currentTenant = await TenantService.getTenantBySlug(tenantSlug);
        
        if (!currentTenant) {
          setError(`Tenant "${tenantSlug}" not found`);
          // Don't try to create default tenant, just use local fallback
          currentTenant = createDefaultTenant();
        }
      } else {
        // For new users (especially during email confirmation), just use local fallback
        // This avoids 406 errors when user hasn't created a workspace yet
        currentTenant = createDefaultTenant();
      }

      if (currentTenant) {
        setTenant(currentTenant);
      } else {
        // Ultimate fallback
        setTenant(createDefaultTenant());
        setError('Unable to load tenant configuration');
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize tenant');
      // Set fallback tenant
      setTenant(createDefaultTenant());
    } finally {
      setLoading(false);
    }
  };

  const switchTenant = async (slug: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const newTenant = await TenantService.getTenantBySlug(slug);
      
      if (!newTenant) {
        throw new Error(`Tenant "${slug}" not found`);
      }

      setTenant(newTenant);

      // Update URL if needed (for development/testing)
      if (import.meta.env.DEV) {
        const url = new URL(window.location.href);
        url.searchParams.set('tenant', slug);
        window.history.replaceState({}, '', url.toString());
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch tenant');
    } finally {
      setLoading(false);
    }
  };

  const refreshTenant = async () => {
    await initializeTenant();
  };

  const contextValue: TenantContextType = {
    tenant,
    loading,
    error,
    switchTenant,
    refreshTenant,
    isMultiTenant
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Convenience hooks
export const useTenantBranding = () => {
  const { tenant } = useTenant();
  return tenant?.branding;
};

export const useTenantSettings = () => {
  const { tenant } = useTenant();
  return tenant?.settings;
};

export const useTenantFeatures = () => {
  const { tenant } = useTenant();
  return tenant?.settings?.features;
};

export const useIsFeatureEnabled = (feature: keyof NonNullable<Tenant['settings']['features']>) => {
  const features = useTenantFeatures();
  return features?.[feature] ?? false;
}; 